import React from 'react'
import EditorAutoPositionAudio from '../__autoPositionAudio/editor__autoPositionAudio'
import Dropdown from 'blocks.simple/dropdown/dropdown'
import EditText from 'blocks.simple/editText/editText'
import Checkbox from 'blocks.simple/checkbox/checkbox'
import translate from 'core/translate'
import { cn } from 'ethcss'
import styles from './editor__areaSettings.jcss'
import grid from 'blocks.simple/grid/grid.jcss'
import item from 'blocks.simple/item/item.jcss'
import { OrderMode } from 'core/models/Editor'
import Collapse from 'blocks.simple/collapse/collapse'
import { editor } from '../editor.local.methods'
import { api } from '../../../core/api/ConnectionManager'
import helpers from '../../../core/helpers'
import { AdvertisementIdAdd } from '../../../pages/broadcasts/_advertisementIds/_add/AdvertisementIdAdd'

const contentModeOptionsList = [OrderMode.userProvided, OrderMode.shuffle]

interface Position {
    left: number
    top: number
    width: number
    height: number
}

interface Option {
    id: string | number
    name: string
}

interface EditorAreaSettingsProps {
    options: Option[] | Record<string, any>
    position: Position
    area: string | number
    onSelect: (value: any) => void
    onEndTyping: () => boolean
    resolutionWidth: number
    resolutionHeight: number
    onAutoPosContent: (mode: string) => void
    onAutoPosSelectedContent: (mode: string) => void
    onToggleLoop: () => void
    autoPos: string
    loop: boolean
    isSimple: boolean
    showAllAreas: boolean
    onSetContentOrderMode: (mode: string) => void
    contentOrderMode: string
    areaContent: Array<{ source: { fileType: string } }>
    noCollapse?: boolean
    areaAdvertisementId: any
}

interface EditorAreaSettingsState {
    options: Option[]
    position: Position
    isWidthNegative: boolean
    isHeightNegative: boolean
    areaAdvertisementId: string
    adError: boolean
    adList: { id: number; name: string }[]
    showAdHelp: boolean
    advertisementIdCreate: boolean
    newAdvertisementId: string
}

class EditorAreaSettings extends React.Component<EditorAreaSettingsProps, EditorAreaSettingsState> {
    contentModeOptions: Option[]

    constructor(p_: EditorAreaSettingsProps) {
        super(p_)

        this.state = {
            options: this.getOptions(p_),
            position: this.getPosition(p_),
            isWidthNegative: false,
            isHeightNegative: false,
            areaAdvertisementId: p_.areaAdvertisementId ? p_.areaAdvertisementId.name : '',
            newAdvertisementId: '',
            adError: false,
            adList: [],
            showAdHelp: false,
            advertisementIdCreate: false,
        }

        this.contentModeOptions = this.getContentModeOptions(contentModeOptionsList)
        this.getOptions = this.getOptions.bind(this)
        this.onChange = this.onChange.bind(this)
        this.getRelPosition = this.getRelPosition.bind(this)
        this.onEndTyping = this.onEndTyping.bind(this)

        p_.onSetContentOrderMode(p_.contentOrderMode)
    }

    componentWillReceiveProps(p_: EditorAreaSettingsProps) {
        const s_ = this.state
        if (p_.position.width < 0) {
            return this.setState({ ...s_, isWidthNegative: true })
        } else if (p_.position.height < 0) {
            return this.setState({ ...s_, isHeightNegative: true })
        } else {
            this.setState({
                options: this.getOptions(p_),
                position: this.getPosition(p_),
                isHeightNegative: false,
                isWidthNegative: false,
            })
        }

        this.setState({
            areaAdvertisementId: !p_.areaAdvertisementId ? '' : p_.areaAdvertisementId.name,
            adError: false,
        })
    }

    componentDidUpdate(
        prevProps: Readonly<EditorAreaSettingsProps>,
        prevState: Readonly<EditorAreaSettingsState>,
        snapshot?: any
    ) {
        if (JSON.stringify(prevProps.areaAdvertisementId) !== JSON.stringify(this.props.areaAdvertisementId)) {
            this.setState({ showAdHelp: false })
        }
    }

    componentDidMount() {
        api.send('getAdvertisementIds', { limit: 1000 }).then((res: any) => {
            if (res && res.data) {
                this.setState({ adList: res.data })
            }
        })

        //@ts-ignore
        if (editor.adIdIsExist()) {
            //@ts-ignore
            editor.enableLoop()
        }

        document.addEventListener('click', this.clickOutside)
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.clickOutside)
    }

    clickOutside = () => {
        this.setState({ showAdHelp: false })
    }

    // Опции для выбора редактируемой зоны
    getOptions(p_: EditorAreaSettingsProps): Option[] {
        return [
            { id: 'allAreas', name: translate('allAreas') },
            ...p_.options.map((option: any, index: number) => ({
                id: index,
                name: `${translate('area')} ${index + 1}`,
            })),
        ]
    }

    getContentModeOptions = (options: string[]) => {
        return [...options.map((option) => ({ id: option, name: translate(option) }))]
    }

    // Обновление позиции по таймеру окончания набора текста
    onEndTyping() {
        const p_ = this.props
        const isUpdated = p_.onEndTyping()

        if (!isUpdated) {
            this.setState({
                position: this.getPosition(p_),
            })
        }
    }

    // Получить позицию в абсолютных координатах в пикселях
    getPosition(p_: EditorAreaSettingsProps): Position {
        return {
            top: Math.round(p_.position.top * p_.resolutionHeight),
            height: Math.round(p_.position.height * p_.resolutionHeight),
            left: Math.round(p_.position.left * p_.resolutionWidth),
            width: Math.round(p_.position.width * p_.resolutionWidth),
        }
    }

    // Получить позицию в относительных координатах
    getRelPosition(): Position {
        const { position } = this.state
        const { resolutionHeight, resolutionWidth } = this.props

        return {
            top: position.top / resolutionHeight,
            height: position.height / resolutionHeight,
            left: position.left / resolutionWidth,
            width: position.width / resolutionWidth,
        }
    }

    // Изменение позиции зоны
    onChange(value: number, field: keyof Position) {
        const s_ = this.state
        const position = s_.position
        position[field] = value

        this.setState({ position })
    }

    onChangeWidthOrHeight(value: number, field: 'width' | 'height') {
        const s_ = this.state
        if (value < 0) {
            field === 'width'
                ? this.setState({ ...s_, isWidthNegative: true })
                : this.setState({ ...s_, isHeightNegative: true })
        } else {
            this.setState({ ...s_, isHeightNegative: false, isWidthNegative: false })
            this.onChange(value, field)
        }
    }

    changeOrderMode = (e: { id: string }) => {
        const p_ = this.props
        p_.onSetContentOrderMode(e.id)
    }

    getAreaTitle = () => {
        return (
            <div className={styles.collapseText}>
                <div className={item.ellipsis}>{translate('areaSettings')}</div>
            </div>
        )
    }

    onChangeAdvertisementId = (areaAdvertisementId: string) => {
        this.setState({ areaAdvertisementId })
    }

    getAdvertisementIds = () => {
        const p_ = this.props
        const s_ = this.state

        this.setState({ adError: false })

        if (!s_.areaAdvertisementId) {
            //@ts-ignore
            editor.updateAreaAdvertisement(p_.area, null)

            if (!s_.adList.length) {
                this.setState({ showAdHelp: false })
            }

            return
        }
    }

    adClear = () => {
        this.setState({ showAdHelp: false, adError: false, areaAdvertisementId: '' })
        // @ts-ignore
        editor.updateAreaAdvertisement(this.props.area, null)
    }

    onChangeAdvId = (field: string, value: string) => {
        this.setState({ newAdvertisementId: value })
    }

    onCloseAdvId = () => {
        this.setState({ newAdvertisementId: '', advertisementIdCreate: false })
    }

    onSaveAdvId = () => {
        api.send('createAdvertisementId', { name: this.state.newAdvertisementId }).then(() => {
            api.send('getAdvertisementIds', { limit: 1000 }).then((res: any) => {
                if (res && res.data) {
                    this.setState({ adList: res.data })
                }
            })
            this.onCloseAdvId()
        })
    }

    renderADIdsHelper = () => {
        const p_ = this.props
        const s_ = this.state
        const list = s_.adList.filter((item) =>
            //s_.areaAdvertisementId.length > 1 &&
            item.name.toLowerCase().includes(s_.areaAdvertisementId.toLowerCase())
        )

        return s_.showAdHelp && !!list.length ? (
            <div className={styles.adIdsHelper}>
                {list.map((item) => (
                    <div
                        className={styles.adListItem}
                        onClick={() => {
                            // @ts-ignore
                            const isValid = editor.adIdIsValid(p_.area, item)

                            if (isValid) {
                                this.onChangeAdvertisementId(item.name)

                                this.setState({ showAdHelp: false })

                                // @ts-ignore
                                editor.updateAreaAdvertisement(p_.area, item)

                                return
                            }

                            this.setState({ showAdHelp: false, adError: true })
                        }}
                    >
                        {item.name}
                    </div>
                ))}
            </div>
        ) : (
            s_.showAdHelp && (
                <div className={styles.adIdsHelper}>
                    <div className={cn(styles.adListItem, styles.adNoIds)}>
                        {translate('noIds')}{' '}
                        <span
                            onClick={() => {
                                this.setState({ advertisementIdCreate: true, areaAdvertisementId: '' })
                            }}
                        >
                            {translate('create')}
                        </span>
                    </div>
                </div>
            )
        )
    }

    render() {
        const s_ = this.state
        const p_ = this.props
        const isShowAutoPositionAudio = !p_.areaContent.some((content) => content.source.fileType !== 'audio')
        const Content = () => {
            return (
                <div>
                    {!p_.isSimple && (
                        <div className={grid.colCenter}>
                            <div className={styles.label}>{translate('area')}</div>
                            <div className={grid.w45}>
                                <Dropdown
                                    value={p_.showAllAreas ? 'allAreas' : p_.area}
                                    options={s_.options}
                                    centered={true}
                                    onChange={p_.onSelect}
                                    isRelativeList={true}
                                />
                            </div>
                        </div>
                    )}
                    {!p_.showAllAreas && (
                        <div>
                            {!p_.isSimple && (
                                <div>
                                    {helpers.isAvailable({ key: 'advertisingAds', action: 'create' }) && (
                                        <div className={cn(styles.row, styles.adIdsWrapper)}>
                                            <EditText
                                                containerClass={cn(styles.inputWrapperFull)}
                                                value={s_.areaAdvertisementId}
                                                label={translate('areaAdvertisementId')}
                                                onChange={(value: string) => {
                                                    this.onChangeAdvertisementId(value)
                                                    this.setState({ showAdHelp: true })
                                                }}
                                                indentSize={'mini'}
                                                onEndTyping={this.getAdvertisementIds}
                                                errorBorder={s_.adError}
                                                errorText={s_.adError && translate('areaAdvertisementIdError')}
                                                placeholder={translate('enterAdvertisementId')}
                                                icon={s_.areaAdvertisementId && 'close'}
                                                showIconOnHover={true}
                                                iconSize={12}
                                                onIconClick={this.adClear}
                                                onClick={(e: any) => {
                                                    e.nativeEvent.stopImmediatePropagation()
                                                    this.setState({ showAdHelp: true })
                                                }}
                                            />
                                            {this.renderADIdsHelper()}
                                        </div>
                                    )}
                                    <div className={styles.row}>
                                        <EditText
                                            type={'number'}
                                            containerClass={cn(styles.inputWrapper)}
                                            value={s_.position.left}
                                            label={translate('leftIndent')}
                                            onChange={(value: number) => this.onChange(value, 'left')}
                                            indentSize={'mini'}
                                            onEndTyping={this.onEndTyping}
                                        />
                                        <EditText
                                            type={'number'}
                                            containerClass={cn(styles.inputWrapper)}
                                            value={s_.position.top}
                                            label={translate('topIndent')}
                                            onChange={(value: number) => this.onChange(value, 'top')}
                                            indentSize={'mini'}
                                            onEndTyping={this.onEndTyping}
                                        />
                                    </div>
                                    <div className={styles.heightAndWidthRow}>
                                        <span className={cn(grid.col, grid.w45)}>
                                            <EditText
                                                type={'number'}
                                                value={s_.position.width}
                                                label={translate('width')}
                                                onChange={(value: number) => this.onChangeWidthOrHeight(value, 'width')}
                                                indentSize={'mini'}
                                                onEndTyping={this.onEndTyping}
                                                validationBorder={s_.isWidthNegative}
                                            />
                                            {s_.isWidthNegative && (
                                                <p className={cn(grid.mt_micro, item.errorInputText)}>
                                                    {translate('valueCannotBeLessThanZero')}
                                                </p>
                                            )}
                                        </span>
                                        <span className={cn(grid.col, grid.w45)}>
                                            <EditText
                                                type={'number'}
                                                value={s_.position.height}
                                                label={translate('height')}
                                                onChange={(value: number) =>
                                                    this.onChangeWidthOrHeight(value, 'height')
                                                }
                                                indentSize={'mini'}
                                                onEndTyping={this.onEndTyping}
                                                validationBorder={s_.isHeightNegative}
                                            />
                                            {s_.isHeightNegative && (
                                                <p className={cn(grid.mt_micro, item.errorInputText)}>
                                                    {translate('valueCannotBeLessThanZero')}
                                                </p>
                                            )}
                                        </span>
                                    </div>
                                </div>
                            )}
                            <div className={styles.checkBox}>
                                <Checkbox
                                    label={translate('contentWithoutSpaces')}
                                    checked={p_.autoPos === 'withoutSpaces'}
                                    onClick={() => p_.onAutoPosContent('withoutSpaces')}
                                />
                            </div>
                            <div className={styles.checkBox}>
                                <Checkbox
                                    label={translate('contentLooping')}
                                    checked={p_.loop}
                                    onClick={p_.onToggleLoop}
                                    //@ts-ignore
                                    disabled={editor.adIdIsExist()}
                                    className={'test-broad-set-nonact-chbx'}
                                />
                            </div>
                            {this.contentModeOptions && (
                                <div className={styles.row}>
                                    <Dropdown
                                        className={cn(grid.w100)}
                                        value={p_.contentOrderMode}
                                        options={this.contentModeOptions}
                                        label={translate('playOrder')}
                                        onChange={this.changeOrderMode}
                                        isRelativeList={true}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    {isShowAutoPositionAudio && p_.areaContent.length > 0 && (
                        <div>
                            <div className={cn(styles.title, styles.borderTop, grid.mt_big)}>
                                <div className={item.ellipsis}>{translate('autoPositionAudio')}</div>
                            </div>
                            <EditorAutoPositionAudio content={p_.areaContent} soundOrderMode={p_.contentOrderMode} />
                        </div>
                    )}
                    {s_.advertisementIdCreate && (
                        <AdvertisementIdAdd
                            advertisementId={{ id: 0, name: s_.newAdvertisementId }}
                            onChange={this.onChangeAdvId}
                            onClose={this.onCloseAdvId}
                            onSave={this.onSaveAdvId}
                        />
                    )}
                </div>
            )
        }

        return (
            <div>
                {!p_.noCollapse ? (
                    <Collapse
                        title={() => this.getAreaTitle()}
                        titleClassName={styles.collapseTitle}
                        activeClassName={styles.collapseActive}
                        className={styles.collapse}
                        withoutOverflow={true}
                        initialOpen={true}
                    >
                        {Content()}
                    </Collapse>
                ) : (
                    Content()
                )}
            </div>
        )
    }
}

export default EditorAreaSettings
